/* VENDORED — DO NOT EDIT BY HAND. See scripts/vendor-manifest.mjs.
   source:  andavo-dev libs/client/shared/shared/util/api/src/api.ts
   commit:  e4d5051bce
   repoint: this file maps to @andavo/* at merge into andavo-dev.
 */
import type { Api } from './types';

import { ApiError, AuthTokenUnavailableError, RateLimitError } from './errors';

import { isLocalBaseUrl, makeLocalUrl } from './localPortRouting';

function parseContentDispositionFilename(
  header: string | null,
): string | undefined {
  if (!header) return undefined;
  // Try RFC 5987 encoded filename* first, then plain filename
  const utf8Match = header.match(/filename\*=UTF-8''([^;\s]+)/i);
  if (utf8Match) return decodeURIComponent(utf8Match[1]);
  const match = header.match(/filename="([^"]+)"/);
  return match?.[1];
}

// Auth resolver contract: platform-specific code registers implementations here
export type AuthResolvers = {
  getAccessToken: () => Promise<string | undefined>;
  clearCredentials: () => void | Promise<void>;
};

type AnalyticsResolvers = {
  getAnalyticsId: () => Promise<string | undefined>;
};

type PartialAnalyticsResolvers = Partial<AnalyticsResolvers>;

const noop = () => Promise.resolve();
const noopString: () => Promise<string | undefined> = () =>
  Promise.resolve(undefined);

const resolvers: AuthResolvers = {
  getAccessToken: noopString,
  clearCredentials: noop,
};

let analyticsResolvers: AnalyticsResolvers = {
  getAnalyticsId: noopString,
};

export function setAnalyticsResolvers(partial: PartialAnalyticsResolvers) {
  analyticsResolvers = {
    ...analyticsResolvers,
    ...partial,
  };
}

// Runtime-configurable API settings
// Default to empty values and rely on configureApi() at app bootstrap
let currentBaseUrl = '';
let currentAppVersion = '';

export function configureApi(config: {
  baseUrl?: string;
  appVersion?: string;
  getAccessToken?: () => Promise<string | undefined>;
  clearCredentials?: () => void | Promise<void>;
}) {
  if (typeof config.baseUrl === 'string') currentBaseUrl = config.baseUrl;
  if (typeof config.appVersion === 'string')
    currentAppVersion = config.appVersion;
  if (config.getAccessToken) resolvers.getAccessToken = config.getAccessToken;
  if (config.clearCredentials)
    resolvers.clearCredentials = config.clearCredentials;
}

async function resolveAnalyticsId(): Promise<string | undefined> {
  try {
    return await analyticsResolvers.getAnalyticsId();
  } catch {
    return undefined;
  }
}

function getHeaders(
  accessToken: string,
  analyticsId: string | undefined,
  rest?: HeadersInit,
): HeadersInit {
  return {
    Authorization: `Bearer ${accessToken}`,
    ...(analyticsId ? { 'X-Andavo-Analytics-Id': analyticsId } : {}),
    'X-Andavo-Client-Version': currentAppVersion || '',
    ...rest,
  };
}

function makeUrl(path: string) {
  if (isLocalBaseUrl(currentBaseUrl)) {
    return makeLocalUrl(currentBaseUrl, path);
  }
  return currentBaseUrl === '' ? path : `${currentBaseUrl}/${path}`;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    // clear credentials on unauthorized
    try {
      await resolvers.clearCredentials();
    } catch {
      // ignore
    }
    return Promise.reject(await response.text());
  }
  const contentType = response.headers.get('content-type') ?? '';
  if (
    contentType.includes('application/pdf') ||
    contentType.includes('application/zip')
  ) {
    const blob = await response.blob();
    const filename = parseContentDispositionFilename(
      response.headers.get('content-disposition'),
    );
    return { blob, filename } as T;
  }
  const text = await response.text();
  let data: Record<string, unknown> = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new ApiError('Failed to parse response body', response.status);
  }
  if (response.status === 429) {
    const retryAfter =
      parseFloat(response.headers.get('Retry-After') ?? '') ||
      (data?.retryAfterSeconds as number) ||
      30;
    throw new RateLimitError(
      (data?.error as string) ||
        (data?.message as string) ||
        'Too many requests',
      retryAfter,
    );
  }
  if (!response.ok) {
    const error = (data?.message as string) || response.statusText;
    throw new ApiError(error, response.status);
  }
  return data as T;
}

async function resolveAccessToken(): Promise<string> {
  // Retry once to handle transient failures (network blips, temporary Auth0
  // unavailability). If we can't obtain a token — whether getAccessToken()
  // throws or returns undefined — we throw AuthTokenUnavailableError to
  // prevent sending a request without an Authorization header. An
  // unauthenticated request would 401 and trigger clearCredentials, logging
  // the user out over what may be a transient token failure or a cold-start
  // race where Auth0 hasn't initialised yet.
  //
  // We intentionally do NOT call clearCredentials here because it clears
  // Auth0's DPoP key pair. If an authorize flow is in progress (e.g. user
  // just signed in and the token exchange hasn't completed), calling
  // clearCredentials would wipe the DPoP keys and cause a JKT mismatch.
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const token = await resolvers.getAccessToken();
      if (token) return token;
      break; // undefined is definitive — don't retry, just bail
    } catch {
      if (attempt === 0) continue;
    }
  }
  throw new AuthTokenUnavailableError();
}

export const api: Api = {
  get baseUrl() {
    return currentBaseUrl;
  },
  async get<T>(url: string, options?: RequestInit) {
    const accessToken = await resolveAccessToken();
    const analyticsId = await resolveAnalyticsId();
    const requestOptions: RequestInit = {
      method: 'GET',
      ...options,
      headers: getHeaders(accessToken, analyticsId, options?.headers),
    };
    const response = await fetch(makeUrl(url), requestOptions);
    return handleResponse<T>(response);
  },
  async post<T>(url: string, body: unknown, options?: RequestInit) {
    const accessToken = await resolveAccessToken();
    const analyticsId = await resolveAnalyticsId();
    const requestOptions: RequestInit = {
      method: 'POST',
      headers: getHeaders(accessToken, analyticsId, {
        'Content-Type': 'application/json',
        ...options?.headers,
      }),
      ...(body ? { body: JSON.stringify(body) } : {}),
      ...options,
    };
    const response = await fetch(makeUrl(url), requestOptions);
    return handleResponse<T>(response);
  },
  async put<T>(url: string, body: unknown, options?: RequestInit) {
    const accessToken = await resolveAccessToken();
    const analyticsId = await resolveAnalyticsId();
    const requestOptions: RequestInit = {
      method: 'PUT',
      headers: getHeaders(accessToken, analyticsId, {
        'Content-Type': 'application/json',
        ...options?.headers,
      }),
      body: JSON.stringify(body),
      ...options,
    };
    const response = await fetch(makeUrl(url), requestOptions);
    return handleResponse<T>(response);
  },
  async patch<T>(url: string, body?: unknown, options?: RequestInit) {
    const accessToken = await resolveAccessToken();
    const analyticsId = await resolveAnalyticsId();
    const requestOptions: RequestInit = {
      method: 'PATCH',
      headers: getHeaders(accessToken, analyticsId, {
        'Content-Type': 'application/json',
        ...options?.headers,
      }),
      ...(body ? { body: JSON.stringify(body) } : {}),
      ...options,
    };
    const response = await fetch(makeUrl(url), requestOptions);
    return handleResponse<T>(response);
  },
  async delete<T>(url: string, options?: RequestInit) {
    const accessToken = await resolveAccessToken();
    const analyticsId = await resolveAnalyticsId();
    const requestOptions: RequestInit = {
      method: 'DELETE',
      headers: getHeaders(accessToken, analyticsId, options?.headers),
      ...options,
    };
    const response = await fetch(makeUrl(url), requestOptions);
    return handleResponse<T>(response);
  },
};
